// Share-link payload fetcher with a built-in offline fixture path.
//
// In production this calls `GET ${API_BASE}/share-links/:code`. In dev — or
// during Playwright runs — the `?fixture=1` query param short-circuits the
// network and returns a hardcoded payload that points at the sample assets
// shipped under `public/sample/`. This means a developer can boot the viewer
// without the backend running and still exercise the iOS / desktop branches.

import { API_BASE } from "./env.ts";
import type { SharePayload, ShareFetchResult } from "./types.ts";

/** Hardcoded payload used by `?fixture=1`. Asset paths are relative to the
 *  web root so Vite serves them out of `public/`. */
export const FIXTURE_PAYLOAD: SharePayload = {
  deckName: "Sample 12 x 16 cedar deck",
  contractorName: "Demo Decks & Co.",
  contractorLogoUrl: undefined,
  usdzUrl: "/sample/deck.usdz",
  glbUrl: "/sample/deck.glb",
  heroImageUrl: "/sample/hero.svg",
  totalCents: 1842500,
  acceptedDisclaimer:
    "IRC 2021 prescriptive defaults. Verify with local jurisdiction. Not a stamped engineering document.",
};

export async function fetchSharePayload(
  code: string,
  fixture: boolean,
  fetchImpl: typeof fetch = fetch,
): Promise<ShareFetchResult> {
  if (fixture) {
    return { kind: "ok", payload: FIXTURE_PAYLOAD };
  }

  const url = `${API_BASE}/share-links/${encodeURIComponent(code)}`;
  let response: Response;
  try {
    response = await fetchImpl(url, {
      headers: { accept: "application/json" },
      credentials: "omit",
    });
  } catch (err) {
    return {
      kind: "error",
      message: err instanceof Error ? err.message : "Network error",
    };
  }

  if (response.status === 404) return { kind: "not-found" };
  // 410 Gone is the canonical status for a revoked / expired link.
  if (response.status === 410) return { kind: "expired" };
  if (!response.ok) {
    return {
      kind: "error",
      message: `Request failed: ${response.status} ${response.statusText}`,
    };
  }

  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch {
    return { kind: "error", message: "Malformed response from server" };
  }

  if (!isBackendSharePayload(parsed)) {
    return { kind: "error", message: "Unexpected response shape" };
  }
  return { kind: "ok", payload: toSharePayload(parsed) };
}

// ---------------------------------------------------------------------------
// Backend contract + adapter.
//
// `GET /share-links/:code` (backend/src/routes/share-links.ts) returns a
// nested `{estimate, job, customer, company, assets}` document; the viewer
// renders the flat `SharePayload`. This adapter is the ONLY place that has
// to track the backend response shape.

interface BackendAsset {
  kind: string;
  contentType: string;
  /** Short-lived signed download URL resolved by the backend. */
  url: string;
}

interface BackendSharePayload {
  estimate: { totalCents: number | null };
  job: { jobName: string };
  company: { name: string; branding: { logoUrl?: string | null } | null };
  assets: BackendAsset[];
}

function isBackendSharePayload(value: unknown): value is BackendSharePayload {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  const estimate = v.estimate as Record<string, unknown> | null | undefined;
  const job = v.job as Record<string, unknown> | null | undefined;
  const company = v.company as Record<string, unknown> | null | undefined;
  return (
    !!estimate &&
    typeof estimate === "object" &&
    (typeof estimate.totalCents === "number" || estimate.totalCents === null) &&
    !!job &&
    typeof job.jobName === "string" &&
    !!company &&
    typeof company.name === "string" &&
    Array.isArray(v.assets) &&
    v.assets.every(
      (a: unknown) =>
        !!a &&
        typeof a === "object" &&
        typeof (a as Record<string, unknown>).kind === "string" &&
        typeof (a as Record<string, unknown>).url === "string",
    )
  );
}

function toSharePayload(backend: BackendSharePayload): SharePayload {
  const usdz = backend.assets.find((a) => a.kind === "USDZ");
  // GLBs arrive as HouseModel assets today; match on content type so a
  // future dedicated deck-GLB kind maps without another adapter change.
  const glb = backend.assets.find(
    (a) => a.contentType === "model/gltf-binary",
  );
  const hero = backend.assets.find((a) => a.kind === "Photo");
  return {
    deckName: backend.job.jobName,
    contractorName: backend.company.name,
    contractorLogoUrl: backend.company.branding?.logoUrl ?? undefined,
    usdzUrl: usdz?.url,
    glbUrl: glb?.url,
    heroImageUrl: hero?.url,
    // Null total (estimate not priced yet) renders as "no price shown".
    totalCents: backend.estimate.totalCents ?? 0,
    // The share payload doesn't carry a disclaimer yet; the footer hides
    // the block when absent.
    acceptedDisclaimer: undefined,
  };
}
